import React, { useState, useRef } from 'react';
import styles from './Modal.module.css';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Dashboard from '../Dashboard/Dashboard';
import { Textarea, Button } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, 
  faUpload, 
  faVideo, 
  faFileImage,
  faFilm 
} from '@fortawesome/free-solid-svg-icons';

interface ThumbnailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProcessContent: (description?: string) => Promise<string>;
  mediaType: 'video' | 'audio' | 'none';
  mediaUrl?: string;
  isProcessing: boolean;
  showDashboard: boolean;
  jobId: string | null;
  onCompletionCallback: (projectId: string) => void;
  projectId?: string;
}

const ThumbnailModal: React.FC<ThumbnailModalProps> = ({ 
  isOpen, 
  onClose, 
  onProcessContent,
  mediaType,
  mediaUrl,
  isProcessing,
  showDashboard,
  jobId,
  onCompletionCallback,
  projectId
}) => {
  let navigate: (path: string) => void;
  try {
    navigate = useNavigate();
  } catch (error) {
    navigate = (path: string) => {
      console.warn('Navigation attempted outside Router context:', path);
    };
  }

  const [description, setDescription] = useState('');
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'description' | 'process' | 'upload' | 'extract'>('description');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const hiddenProjectIdRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const handleProcessContent = async () => {
    try {
      let resultProjectId: string;
      
      if (selectedMethod === 'description' && description.trim()) {
        resultProjectId = await onProcessContent(description.trim());
      } else if (selectedMethod === 'process' && (mediaUrl || projectId)) {
        resultProjectId = await onProcessContent();
      } else {
        console.error('Invalid method or missing data');
        return;
      }
      
      if (resultProjectId) {
        onCompletionCallback(resultProjectId);
      }
    } catch (error) {
      console.error('Error processing thumbnail:', error);
    }
  };

  const handleUploadImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      // Handle image upload
      console.log('Image uploaded:', file.name);
      // TODO: Implement image upload logic
    }
  };

  const renderMethodSelection = () => (
    <div className={styles.methodSelection}>
      <h3>Choose Thumbnail Generation Method</h3>
      
      <div className={styles.methodOptions}>
        <button
          className={`${styles.methodButton} ${selectedMethod === 'description' ? styles.selected : ''}`}
          onClick={() => setSelectedMethod('description')}
        >
          <FontAwesomeIcon icon={faImage} className={styles.methodIcon} />
          <span>Generate from Description</span>
        </button>

        {(mediaUrl || projectId) && (
          <button
            className={`${styles.methodButton} ${selectedMethod === 'process' ? styles.selected : ''}`}
            onClick={() => setSelectedMethod('process')}
          >
            <FontAwesomeIcon icon={faVideo} className={styles.methodIcon} />
            <span>Process with Existing {mediaType === 'video' ? 'Video' : 'Audio'}</span>
          </button>
        )}

        <button
          className={`${styles.methodButton} ${selectedMethod === 'upload' ? styles.selected : ''}`}
          onClick={() => setSelectedMethod('upload')}
        >
          <FontAwesomeIcon icon={faFileImage} className={styles.methodIcon} />
          <span>Upload Image</span>
        </button>

        {mediaUrl && mediaType === 'video' && (
          <button
            className={`${styles.methodButton} ${selectedMethod === 'extract' ? styles.selected : ''}`}
            onClick={() => setSelectedMethod('extract')}
          >
            <FontAwesomeIcon icon={faFilm} className={styles.methodIcon} />
            <span>Extract Frame from Video</span>
          </button>
        )}
      </div>
    </div>
  );

  const renderMethodContent = () => {
    switch (selectedMethod) {
      case 'description':
        return (
          <div className={styles.descriptionSection}>
            <label htmlFor="thumbnailDescription">Describe your thumbnail:</label>
            <Textarea
              id="thumbnailDescription"
              placeholder="E.g., 'Epic gaming thumbnail with lightning effects and bold text'"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              minRows={3}
              maxRows={5}
              className={styles.descriptionInput}
            />
          </div>
        );
      
      case 'process':
        return (
          <div className={styles.processSection}>
            <p>Generate thumbnail using existing {mediaType === 'video' ? 'video' : 'audio'} content and captions.</p>
            {description.trim() && (
              <div className={styles.optionalDescription}>
                <label htmlFor="additionalDescription">Additional description (optional):</label>
                <Textarea
                  id="additionalDescription"
                  placeholder="Additional guidance for AI generation..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  minRows={2}
                  maxRows={3}
                />
              </div>
            )}
          </div>
        );
      
      case 'upload':
        return (
          <div className={styles.uploadSection}>
            <p>Upload an image to edit and enhance for your thumbnail.</p>
            <Button onClick={handleUploadImage} className={styles.uploadButton}>
              <FontAwesomeIcon icon={faUpload} />
              Choose Image File
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>
        );
      
      case 'extract':
        return (
          <div className={styles.extractSection}>
            <p>Extract a frame from your video to use as a thumbnail base.</p>
            <small>Frame will be extracted at 5 seconds by default.</small>
          </div>
        );
      
      default:
        return null;
    }
  };
  const canProcess = () => {
    switch (selectedMethod) {
      case 'description':
        return description.trim().length > 0;
      case 'process':
        return Boolean(mediaUrl || projectId);
      case 'upload':
        return false; // Will be handled separately
      case 'extract':
        return Boolean(mediaUrl && mediaType === 'video');
      default:
        return false;
    }
  };

  if (showDashboard) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.dashboardModal}>
          <Dashboard 
            jobId={jobId} 
            callBackFunc={(videoUrl: string, projectId: string) => {
              console.log('Dashboard callback:', videoUrl, projectId);
              // Handle the callback as needed
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Create Thumbnail</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Media Preview Section */}
          {mediaUrl && (
            <div className={styles.mediaPreview}>
              <h4>Current Media:</h4>
              {mediaType === 'video' ? (
                <video
                  src={mediaUrl}
                  controls
                  onLoadedData={() => setMediaLoaded(true)}
                  className={styles.previewVideo}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <audio
                  src={mediaUrl}
                  controls
                  onLoadedData={() => setMediaLoaded(true)}
                  className={styles.previewAudio}
                >
                  Your browser does not support the audio tag.
                </audio>
              )}
            </div>
          )}

          {/* Method Selection */}
          {renderMethodSelection()}

          {/* Method-specific Content */}
          {renderMethodContent()}

          {/* Hidden project ID reference */}
          <div ref={hiddenProjectIdRef} style={{ display: 'none' }}>
            {projectId}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.processButton}
            onClick={handleProcessContent}
            disabled={!canProcess() || isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Generate Thumbnail'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThumbnailModal;